import type { Message, Seq } from '@cyphex/types';
import { GROUPING_WINDOW_MS } from '../constants';
import type { OutboxItem, TimelineRow } from '../types';
import { isSameDayIso } from './time';

/**
 * Row-height model, kept in step with `MessageBubble`'s Tailwind classes.
 *
 * These are only pre-measure estimates — the virtualiser measures for real the
 * moment a row mounts — but a wrong estimate is a visible scroll jump, so they
 * track the compact Teams metrics rather than the old airy ones.
 */
/** One line of `text-md` (14px / 1.375rem). */
const LINE_PX = 22;
/** Bubble `py-1.5` plus the row's `pt-2 pb-px` on the first of a run. */
const FIRST_ROW_CHROME_PX = 38;
/** What a continuation saves: the header strip and the group's top padding. */
const GROUPED_SAVING_PX = 24;
/** Bubble padding plus `py-px` — a one-line continuation can be this short. */
const MIN_ROW_PX = 36;

interface BuildRowsInput {
  messages: readonly Message[];
  pending: ReadonlyMap<string, OutboxItem>;
  /** Seq of the first message the viewer has not read. Draws the divider. */
  firstUnreadSeq: Seq | null;
  unreadCount: number;
}

function estimate(message: Message): number {
  const chars = message.bodyText?.length ?? 0;
  const lines = Math.max(1, Math.ceil(chars / 72));
  let px = FIRST_ROW_CHROME_PX + lines * LINE_PX;
  if (message.attachments.length > 0) px += 92;
  if (message.previews.length > 0) px += 96;
  if (message.reactions.length > 0) px += 30;
  if (message.voice) px += 28;
  if (message.replyTo) px += 34;
  return px;
}

function continues(previous: Message, current: Message): boolean {
  if (previous.kind === 'system' || current.kind === 'system') return false;
  if (current.replyTo || current.forwardedFrom) return false;
  if (!previous.sender || !current.sender) return false;
  if (previous.sender.id !== current.sender.id) return false;
  const gap = new Date(current.createdAt).getTime() - new Date(previous.createdAt).getTime();
  return gap >= 0 && gap < GROUPING_WINDOW_MS;
}

/**
 * Flattens a message page into the exact row list the virtualiser renders:
 * day dividers, the unread separator, and per-message grouping flags.
 * Pure and memoisable — this runs on every timeline render.
 */
export function buildTimelineRows({
  messages,
  pending,
  firstUnreadSeq,
  unreadCount,
}: BuildRowsInput): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let previous: Message | null = null;
  let unreadDrawn = firstUnreadSeq === null;

  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];
    if (!message) continue;

    if (!previous || !isSameDayIso(previous.createdAt, message.createdAt)) {
      rows.push({
        kind: 'day',
        id: `day_${message.createdAt.slice(0, 10)}`,
        date: message.createdAt,
        sizeHint: 34,
      });
      previous = null;
    }

    if (!unreadDrawn && firstUnreadSeq !== null && message.seq >= firstUnreadSeq) {
      rows.push({ kind: 'unread', id: 'unread_divider', count: unreadCount, sizeHint: 30 });
      unreadDrawn = true;
      previous = null;
    }

    const grouped = previous !== null && continues(previous, message);
    const next = messages[i + 1];
    const groupEnd = !next || !continues(message, next);

    rows.push({
      kind: 'message',
      id: message.id,
      message,
      grouped,
      groupEnd,
      pending: pending.get(message.id) ?? null,
      sizeHint: Math.max(MIN_ROW_PX, estimate(message) - (grouped ? GROUPED_SAVING_PX : 0)),
    });
    previous = message;
  }

  return rows;
}
